import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Upload, X, Loader2, AlertTriangle, Save } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl } from "../../lib/api";
import { PROPERTY_TYPES } from "../../lib/format";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import Seo from "../../components/Seo";

const ASSESSMENTS = ["Excellent Value", "Good Value", "Fair Price", "Above Market", "Premium Pricing"];
const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

const empty = {
  title: "", property_type: "flat", transaction_type: "Sale", price: "", negotiable: true,
  location: "Faridabad", sector: "", address: "", area_sqft: "", bedrooms: "", bathrooms: "",
  floor: "", total_floors: "", parking: "", property_age: "", facing: "", road_width: "", furnishing: "",
  asking_price: "", fair_price_min: "", fair_price_max: "", price_assessment: "",
  score_overall: "", score_price: "", score_location: "", score_construction: "", score_connectivity: "", score_investment: "",
  why_recommend: "", things_to_know: "", locality_connectivity: "", locality_schools: "", locality_hospitals: "",
  locality_market: "", locality_investment: "", images: [], video_url: "", floor_plan_url: "",
  featured: false, urgent_sale: false, price_reduced: false, new_listing: true, personally_verified: true,
  location_verified: false, price_checked: false, photos_verified: false, source_confirmed: false,
  verification_notes: "", last_verified_date: "", verified_by: "", status: "active",
  seo_title: "", seo_description: "",
};

function Field({ label, children, className = "" }) {
  return <div className={className}><label className="text-sm font-medium text-slate-700">{label}</label><div className="mt-1.5">{children}</div></div>;
}
const inp = "w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-700/30";

export default function PropertyForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const editing = !!id;
  const [f, setF] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dup, setDup] = useState(null);
  const fileRef = useRef();

  const { data: locations } = useQuery({ queryKey: ["form-locations"], queryFn: async () => (await api.get("/locations")).data });
  const { data: existing } = useQuery({ queryKey: ["edit-prop", id], queryFn: async () => (await api.get(`/admin/properties`, { params: {} })).data.items.find((x) => x.id === id), enabled: editing });

  useEffect(() => {
    if (existing) {
      const merged = { ...empty };
      Object.keys(empty).forEach((k) => { if (existing[k] !== undefined && existing[k] !== null) merged[k] = existing[k]; });
      merged.why_recommend = (existing.why_recommend || []).join("\n");
      merged.things_to_know = (existing.things_to_know || []).join("\n");
      merged.images = existing.images || [];
      setF(merged);
    }
  }, [existing]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setF((p) => ({ ...p, images: [...p.images, data.url] }));
      }
      toast.success("Uploaded");
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const uploadSingle = async (e, key) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try { const fd = new FormData(); fd.append("file", file); const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); set(key, data.url); toast.success("Uploaded"); }
    catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const buildPayload = () => ({
    ...f,
    price: num(f.price), asking_price: num(f.asking_price), fair_price_min: num(f.fair_price_min), fair_price_max: num(f.fair_price_max),
    area_sqft: num(f.area_sqft), bedrooms: num(f.bedrooms), bathrooms: num(f.bathrooms), parking: num(f.parking),
    score_overall: num(f.score_overall), score_price: num(f.score_price), score_location: num(f.score_location),
    score_construction: num(f.score_construction), score_connectivity: num(f.score_connectivity), score_investment: num(f.score_investment),
    locality_connectivity: num(f.locality_connectivity), locality_schools: num(f.locality_schools), locality_hospitals: num(f.locality_hospitals),
    locality_market: num(f.locality_market), locality_investment: num(f.locality_investment),
    why_recommend: f.why_recommend.split("\n").map((s) => s.trim()).filter(Boolean),
    things_to_know: f.things_to_know.split("\n").map((s) => s.trim()).filter(Boolean),
    nearby: existing?.nearby || {},
  });

  const save = async (force = false) => {
    if (!f.title || !f.sector) return toast.error("Title and Sector are required");
    const payload = buildPayload();
    if (!editing && !force) {
      try { const { data } = await api.post("/admin/properties/check-duplicate", payload); if (data.duplicate) { setDup(data.duplicate); return; } } catch {}
    }
    setSaving(true);
    try {
      if (editing) await api.put(`/admin/properties/${id}`, payload);
      else await api.post("/admin/properties", payload);
      toast.success(editing ? "Property updated" : "Property created");
      nav("/admin/properties");
    } catch (e) { toast.error("Save failed"); } finally { setSaving(false); setDup(null); }
  };

  return (
    <div className="max-w-4xl">
      <Seo title={`${editing ? "Edit" : "Add"} Property | Admin`} />
      <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900">{editing ? "Edit Property" : "Add Property"}</h1>

      {dup && (
        <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3" data-testid="duplicate-warning">
          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
          <div className="flex-1"><p className="text-sm text-amber-900 font-medium">Possible duplicate found: {dup.title} ({dup.property_id})</p>
            <div className="flex gap-2 mt-2"><Button size="sm" onClick={() => save(true)} className="bg-amber-600 hover:bg-amber-700">Continue anyway</Button><Button size="sm" variant="outline" onClick={() => setDup(null)}>Cancel</Button></div></div>
        </div>
      )}

      <div className="space-y-6 mt-6">
        {/* Basic */}
        <section className="bg-white rounded-2xl border border-slate-200 p-6">
          <h2 className="font-heading font-bold text-slate-900 mb-4">Basic Information</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Property Title *" className="sm:col-span-2"><input value={f.title} onChange={(e) => set("title", e.target.value)} className={inp} data-testid="pf-title" /></Field>
            <Field label="Property Type"><Select value={f.property_type} onValueChange={(v) => set("property_type", v)}><SelectTrigger data-testid="pf-type"><SelectValue /></SelectTrigger><SelectContent>{PROPERTY_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent></Select></Field>
            <Field label="Transaction Type"><input value={f.transaction_type} onChange={(e) => set("transaction_type", e.target.value)} className={inp} /></Field>
            <Field label="Price (₹)"><input type="number" value={f.price} onChange={(e) => set("price", e.target.value)} className={inp} data-testid="pf-price" /></Field>
            <Field label="Sector *"><Select value={f.sector} onValueChange={(v) => set("sector", v)}><SelectTrigger data-testid="pf-sector"><SelectValue placeholder="Select sector" /></SelectTrigger><SelectContent>{(locations?.items || []).map((l) => <SelectItem key={l.id} value={l.name}>{l.name}</SelectItem>)}</SelectContent></Select></Field>
            <Field label="Address" className="sm:col-span-2"><input value={f.address} onChange={(e) => set("address", e.target.value)} className={inp} /></Field>
            <div className="flex items-center gap-2"><Switch checked={f.negotiable} onCheckedChange={(v) => set("negotiable", v)} data-testid="pf-negotiable" /><span className="text-sm text-slate-600">Negotiable</span></div>
          </div>
        </section>

        {/* Details */}
        <section className="bg-white rounded-2xl border border-slate-200 p-6">
          <h2 className="font-heading font-bold text-slate-900 mb-4">Property Details</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Field label="Area (sq.ft)"><input type="number" value={f.area_sqft} onChange={(e) => set("area_sqft", e.target.value)} className={inp} /></Field>
            <Field label="Bedrooms"><input type="number" value={f.bedrooms} onChange={(e) => set("bedrooms", e.target.value)} className={inp} /></Field>
            <Field label="Bathrooms"><input type="number" value={f.bathrooms} onChange={(e) => set("bathrooms", e.target.value)} className={inp} /></Field>
            <Field label="Parking"><input type="number" value={f.parking} onChange={(e) => set("parking", e.target.value)} className={inp} /></Field>
            <Field label="Floor"><input value={f.floor} onChange={(e) => set("floor", e.target.value)} className={inp} /></Field>
            <Field label="Total Floors"><input value={f.total_floors} onChange={(e) => set("total_floors", e.target.value)} className={inp} /></Field>
            <Field label="Property Age"><input value={f.property_age} onChange={(e) => set("property_age", e.target.value)} className={inp} /></Field>
            <Field label="Facing"><input value={f.facing} onChange={(e) => set("facing", e.target.value)} className={inp} /></Field>
            <Field label="Road Width"><input value={f.road_width} onChange={(e) => set("road_width", e.target.value)} className={inp} /></Field>
            <Field label="Furnishing"><input value={f.furnishing} onChange={(e) => set("furnishing", e.target.value)} className={inp} /></Field>
          </div>
        </section>

        {/* Pricing */}
        <section className="bg-white rounded-2xl border border-slate-200 p-6">
          <h2 className="font-heading font-bold text-slate-900 mb-4">Pricing & Assessment</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Field label="Asking Price"><input type="number" value={f.asking_price} onChange={(e) => set("asking_price", e.target.value)} className={inp} /></Field>
            <Field label="Fair Price Min"><input type="number" value={f.fair_price_min} onChange={(e) => set("fair_price_min", e.target.value)} className={inp} /></Field>
            <Field label="Fair Price Max"><input type="number" value={f.fair_price_max} onChange={(e) => set("fair_price_max", e.target.value)} className={inp} /></Field>
            <Field label="Price Assessment"><Select value={f.price_assessment || undefined} onValueChange={(v) => set("price_assessment", v)}><SelectTrigger data-testid="pf-assessment"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{ASSESSMENTS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent></Select></Field>
          </div>
        </section>

        {/* Scores */}
        <section className="bg-white rounded-2xl border border-slate-200 p-6">
          <h2 className="font-heading font-bold text-slate-900 mb-4">Property Score (out of 10)</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {[["score_overall", "Overall"], ["score_price", "Price Value"], ["score_location", "Location"], ["score_construction", "Construction"], ["score_connectivity", "Connectivity"], ["score_investment", "Investment"]].map(([k, l]) => (
              <Field key={k} label={l}><input type="number" step="0.1" max="10" value={f[k]} onChange={(e) => set(k, e.target.value)} className={inp} data-testid={`pf-${k}`} /></Field>
            ))}
          </div>
        </section>

        {/* Locality */}
        <section className="bg-white rounded-2xl border border-slate-200 p-6">
          <h2 className="font-heading font-bold text-slate-900 mb-4">Locality Intelligence (out of 10)</h2>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
            {[["locality_connectivity", "Connectivity"], ["locality_schools", "Schools"], ["locality_hospitals", "Hospitals"], ["locality_market", "Market"], ["locality_investment", "Investment"]].map(([k, l]) => (
              <Field key={k} label={l}><input type="number" step="0.1" max="10" value={f[k]} onChange={(e) => set(k, e.target.value)} className={inp} /></Field>
            ))}
          </div>
        </section>

        {/* Recommend */}
        <section className="bg-white rounded-2xl border border-slate-200 p-6">
          <h2 className="font-heading font-bold text-slate-900 mb-4">Why We Recommend / Things to Know</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Why We Recommend (one per line)"><textarea rows={5} value={f.why_recommend} onChange={(e) => set("why_recommend", e.target.value)} className={inp} data-testid="pf-recommend" /></Field>
            <Field label="Things You Should Know (one per line)"><textarea rows={5} value={f.things_to_know} onChange={(e) => set("things_to_know", e.target.value)} className={inp} /></Field>
          </div>
        </section>

        {/* Media */}
        <section className="bg-white rounded-2xl border border-slate-200 p-6">
          <h2 className="font-heading font-bold text-slate-900 mb-4">Media</h2>
          <input ref={fileRef} type="file" accept="image/*" multiple onChange={upload} className="hidden" data-testid="pf-image-input" />
          <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="pf-upload-btn">
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />} Upload Photos
          </Button>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 mt-4">
            {f.images.map((img, i) => (
              <div key={i} className="relative rounded-lg overflow-hidden aspect-square border border-slate-200">
                <img src={fileUrl(img)} alt="" className="w-full h-full object-cover" />
                <button onClick={() => set("images", f.images.filter((_, ix) => ix !== i))} className="absolute top-1 right-1 bg-white/90 rounded-full p-1" data-testid={`pf-remove-img-${i}`}><X className="w-3 h-3" /></button>
              </div>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            <Field label="Video URL"><input value={f.video_url || ""} onChange={(e) => set("video_url", e.target.value)} placeholder="YouTube or uploaded video URL" className={inp} /></Field>
            <Field label="Floor Plan URL"><input value={f.floor_plan_url || ""} onChange={(e) => set("floor_plan_url", e.target.value)} className={inp} /></Field>
          </div>
        </section>

        {/* Status & badges & verification */}
        <section className="bg-white rounded-2xl border border-slate-200 p-6">
          <h2 className="font-heading font-bold text-slate-900 mb-4">Status, Badges & Verification</h2>
          <Field label="Status" className="max-w-xs"><Select value={f.status} onValueChange={(v) => set("status", v)}><SelectTrigger data-testid="pf-status"><SelectValue /></SelectTrigger><SelectContent>{["active", "sold", "hold", "inactive"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent></Select></Field>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
            {[["featured", "Featured"], ["urgent_sale", "Urgent Sale"], ["price_reduced", "Price Reduced"], ["new_listing", "New Listing"], ["personally_verified", "Personally Verified"]].map(([k, l]) => (
              <label key={k} className="flex items-center gap-2 text-sm text-slate-700"><Switch checked={f[k]} onCheckedChange={(v) => set(k, v)} data-testid={`pf-${k}`} /> {l}</label>
            ))}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
            {[["location_verified", "Location Verified"], ["price_checked", "Price Checked"], ["photos_verified", "Photos Verified"], ["source_confirmed", "Source Confirmed"]].map(([k, l]) => (
              <label key={k} className="flex items-center gap-2 text-sm text-slate-700"><Switch checked={f[k]} onCheckedChange={(v) => set(k, v)} /> {l}</label>
            ))}
          </div>
          <div className="grid sm:grid-cols-3 gap-4 mt-4">
            <Field label="Last Verified Date"><input type="date" value={f.last_verified_date || ""} onChange={(e) => set("last_verified_date", e.target.value)} className={inp} /></Field>
            <Field label="Verified By"><input value={f.verified_by || ""} onChange={(e) => set("verified_by", e.target.value)} className={inp} /></Field>
            <Field label="Verification Notes"><input value={f.verification_notes || ""} onChange={(e) => set("verification_notes", e.target.value)} className={inp} /></Field>
          </div>
        </section>

        {/* SEO */}
        <section className="bg-white rounded-2xl border border-slate-200 p-6">
          <h2 className="font-heading font-bold text-slate-900 mb-4">SEO</h2>
          <div className="grid gap-4">
            <Field label="SEO Title"><input value={f.seo_title || ""} onChange={(e) => set("seo_title", e.target.value)} className={inp} /></Field>
            <Field label="Meta Description"><textarea rows={2} value={f.seo_description || ""} onChange={(e) => set("seo_description", e.target.value)} className={inp} /></Field>
          </div>
        </section>

        <div className="flex gap-3 sticky bottom-4">
          <Button onClick={() => save()} disabled={saving} className="bg-indigo-900 hover:bg-indigo-800 shadow-lg" data-testid="pf-save">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} {editing ? "Update Property" : "Create Property"}
          </Button>
          <Button variant="outline" onClick={() => nav("/admin/properties")}>Cancel</Button>
        </div>
      </div>
    </div>
  );
}
